/**
 * Icon sets offered by the Simple-mode "Bộ icon" picker.
 *
 * A sprite set's `url` is the sprite BASE url — the map loads `<url>.json` +
 * `<url>.png` and, on retina screens, `<url>@2x.json` + `<url>@2x.png`.
 *
 * Icons are looked up BY NAME (`icon-image` in the style, e.g. "hospital",
 * "bank", "cafe"), so a set must use the same names as the Vietmap sprite or
 * those POIs lose their icon — the picker warns with "Thiếu N icon".
 *
 * See public/icon-sets/README.md for adding sets.
 */
type IconSetBase = {
  id: string;
  /** Display name (i18n key). */
  name: string;
  /** One-line description (i18n key). */
  description: string;
  /**
   * Visible icon size vs the default 24 px Vietmap POI icon (height factor,
   * width ÷ height) — places POI labels next to the icon (libs/poi-label).
   */
  labelMetrics?: { height: number; aspect: number };
};

export type IconSet =
  /** A ready-made MapLibre sprite (base URL, see above). */
  | (IconSetBase & { kind: "sprite"; url: string })
  /**
   * Recolourable SVG templates (public/icon-sets/<id>, imported from Figma by
   * scripts/import-figma-icons.mjs). The editor renders them with the user's
   * colours into a sprite and uploads it to the API (/icon-sprites).
   */
  | (IconSetBase & { kind: "svg"; path: string });

/** Set used when a style has no `maputnik:icon_set_id` yet. */
export const DEFAULT_ICON_SET_ID = "vietmap";

export const ICON_SETS: IconSet[] = [
  {
    id: "vietmap",
    kind: "sprite",
    name: "Vietmap",
    description: "Bộ icon mặc định của Vietmap",
    url: "https://maps.vietmap.vn/maps/styles/tm/sprite",
    labelMetrics: { height: 1, aspect: 0.83 },
  },
  {
    id: "modern",
    kind: "svg",
    name: "Hiện đại",
    description: "Bộ icon Map UI — tuỳ chỉnh màu theo nhóm",
    path: "icon-sets/modern",
    // 44×54 pin drawn at designHeight 54.
    labelMetrics: { height: 1, aspect: 0.81 },
  },
  {
    id: "flat",
    kind: "svg",
    name: "Tròn phẳng",
    description: "Icon tròn phẳng không ghim — tuỳ chỉnh màu theo nhóm",
    path: "icon-sets/flat",
    // 40 px disc at designHeight 54.
    labelMetrics: { height: 0.74, aspect: 1 },
  },
  {
    id: "pin",
    kind: "svg",
    name: "Ghim nổi",
    description: "Ghim lớn có chấm định vị — tuỳ chỉnh màu theo nhóm",
    path: "icon-sets/pin",
    // 47×68 pin + dot at designHeight 56.
    labelMetrics: { height: 1.21, aspect: 0.69 },
  },
];

/** Icons shown on each picker tile — common POI categories. */
export const ICON_SET_PREVIEW = ["restaurant", "hospital", "bank", "cafe"];
